import { useState } from 'react';
import { DeveloperNav } from '@/components/DeveloperNav';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Code, 
  Copy, 
  Play, 
  CheckCircle, 
  ExternalLink,
  BookOpen,
  Zap,
  Shield,
  Globe,
  Users
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface CodeExample {
  language: string;
  title: string;
  code: string;
}

const codeExamples: CodeExample[] = [
  {
    language: 'javascript',
    title: 'JavaScript/Node.js',
    code: `const response = await fetch('https://snugglecrafters.com/api/v1/stories/generate', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    age: 6,
    gender: 'girl',
    childName: 'Emma',
    interests: ['princesses', 'animals'],
    style: 'magical',
    lesson: 'kindness',
    enableIllustrations: true
  })
});

const story = await response.json();
console.log(story);`
  },
  {
    language: 'python',
    title: 'Python',
    code: `import requests

url = 'https://snugglecrafters.com/api/v1/stories/generate'
headers = {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
}
data = {
    'age': 6,
    'gender': 'girl',
    'childName': 'Emma',
    'interests': ['princesses', 'animals'],
    'style': 'magical',
    'lesson': 'kindness',
    'enableIllustrations': True
}

response = requests.post(url, headers=headers, json=data)
story = response.json()
print(story)`
  },
  {
    language: 'curl',
    title: 'cURL',
    code: `curl -X POST https://snugglecrafters.com/api/v1/stories/generate \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "age": 6,
    "gender": "girl",
    "childName": "Emma",
    "interests": ["princesses", "animals"],
    "style": "magical",
    "lesson": "kindness",
    "enableIllustrations": true
  }'`
  }
];

const endpoints = [
  {
    method: 'POST',
    path: '/v1/stories/generate',
    description: 'Generate a new bedtime story',
    parameters: [
      { name: 'age', type: 'number', required: true, description: 'Child\'s age (2-12)' },
      { name: 'gender', type: 'string', required: true, description: 'Child\'s gender (girl, boy, other)' },
      { name: 'childName', type: 'string', required: false, description: 'Child\'s name for personalization' },
      { name: 'interests', type: 'array', required: true, description: 'Array of interests (max 3)' },
      { name: 'style', type: 'string', required: true, description: 'Story style (funny, adventurous, gentle, magical, educational)' },
      { name: 'lesson', type: 'string', required: true, description: 'Moral lesson for the story' },
      { name: 'enableIllustrations', type: 'boolean', required: false, description: 'Generate AI illustrations' }
    ]
  },
  {
    method: 'GET',
    path: '/v1/stories/{id}',
    description: 'Retrieve a specific story',
    parameters: [
      { name: 'id', type: 'string', required: true, description: 'Story ID' }
    ]
  },
  {
    method: 'POST',
    path: '/v1/stories/{id}/audio',
    description: 'Generate audio narration for a story',
    parameters: [
      { name: 'id', type: 'string', required: true, description: 'Story ID' },
      { name: 'voice', type: 'string', required: false, description: 'Voice type (nova, alloy, echo, fable, onyx, shimmer)' },
      { name: 'speed', type: 'number', required: false, description: 'Speech speed (0.5-2.0)' }
    ]
  },
  {
    method: 'POST',
    path: '/v1/stories/{id}/illustrations',
    description: 'Generate illustrations for a story',
    parameters: [
      { name: 'id', type: 'string', required: true, description: 'Story ID' },
      { name: 'style', type: 'string', required: false, description: 'Illustration style (cartoon, realistic, watercolor, sketch)' }
    ]
  }
];

export default function ApiDocs() {
  const { toast } = useToast();
  const [selectedExample, setSelectedExample] = useState(0);
  const [apiKey, setApiKey] = useState('');
  const [testRequest, setTestRequest] = useState({
    age: 6,
    gender: 'girl',
    childName: 'Emma',
    interests: ['princesses', 'animals'],
    style: 'magical',
    lesson: 'kindness',
    enableIllustrations: false
  });
  const [testResponse, setTestResponse] = useState<any>(null);
  const [testing, setTesting] = useState(false);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Copied',
      description: 'Code copied to clipboard',
    });
  };

  const testApi = async () => {
    if (!apiKey) {
      toast({
        title: 'API Key Required',
        description: 'Please enter your API key to test the endpoint',
        variant: 'destructive',
      });
      return;
    }

    try {
      setTesting(true);
      const response = await fetch('/api/stories/generate', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(testRequest)
      });

      const data = await response.json();
      setTestResponse({
        status: response.status,
        statusText: response.statusText,
        data: data
      });
    } catch (error) {
      setTestResponse({
        status: 'Error',
        statusText: 'Network Error',
        data: { error: error instanceof Error ? error.message : 'Unknown error' }
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <DeveloperNav />
      {/* Hero Section */}
      <div className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              API Documentation
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Build amazing applications with our AI-powered story generation API. 
              Create personalized bedtime stories, educational content, and interactive experiences.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button asChild size="lg">
                <a href="/developer-signup">
                  <Code className="w-4 h-4 mr-2" />
                  Get API Key
                </a>
              </Button>
              <Button asChild size="lg" variant="outline">
                <a href="/developer-portal">
                  <BookOpen className="w-4 h-4 mr-2" />
                  Developer Portal
                </a>
              </Button>
            </div>
          </div>

          {/* Quick Start */}
          <Card className="mb-12">
            <CardHeader>
              <CardTitle className="text-2xl">Quick Start</CardTitle>
              <CardDescription>
                Get up and running with our API in minutes
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-lg font-semibold mb-4">1. Get Your API Key</h3>
                  <p className="text-gray-600 mb-4">
                    Sign up for a developer account and create your first API key.
                  </p>
                  <Button asChild>
                    <a href="/developer-signup">Get Started</a>
                  </Button>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-4">2. Make Your First Request</h3>
                  <p className="text-gray-600 mb-4">
                    Use our interactive API explorer to test endpoints.
                  </p>
                  <Button variant="outline" onClick={() => document.getElementById('api-explorer')?.scrollIntoView()}>
                    Try It Now
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Features */}
      <div className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            API Features
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Fast Generation</h3>
              <p className="text-gray-600">
                Generate stories in under 10 seconds with our optimized AI models
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Secure & Reliable</h3>
              <p className="text-gray-600">
                Enterprise-grade security with 99.9% uptime and rate limiting
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">RESTful API</h3>
              <p className="text-gray-600">
                Standard HTTP methods with JSON responses and clear error codes
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Developer Support</h3>
              <p className="text-gray-600">
                Comprehensive documentation, SDKs, and dedicated support team
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* API Explorer */}
      <div id="api-explorer" className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">API Explorer</CardTitle>
              <CardDescription>
                Test our API endpoints directly in your browser
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div>
                  <Label htmlFor="apiKey">API Key</Label>
                  <Input
                    id="apiKey"
                    type="password"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder="Enter your API key"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="age">Age</Label>
                    <Input
                      id="age"
                      type="number"
                      value={testRequest.age}
                      onChange={(e) => setTestRequest(prev => ({ ...prev, age: parseInt(e.target.value) }))}
                      min="2"
                      max="12"
                    />
                  </div>
                  <div>
                    <Label htmlFor="gender">Gender</Label>
                    <Select value={testRequest.gender} onValueChange={(value) => setTestRequest(prev => ({ ...prev, gender: value }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="girl">Girl</SelectItem>
                        <SelectItem value="boy">Boy</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="childName">Child Name (Optional)</Label>
                  <Input
                    id="childName"
                    value={testRequest.childName}
                    onChange={(e) => setTestRequest(prev => ({ ...prev, childName: e.target.value }))}
                    placeholder="Emma"
                  />
                </div>

                <div>
                  <Label htmlFor="interests">Interests (comma-separated)</Label>
                  <Input
                    id="interests"
                    value={testRequest.interests.join(', ')}
                    onChange={(e) => setTestRequest(prev => ({ ...prev, interests: e.target.value.split(',').map(s => s.trim()) }))}
                    placeholder="princesses, animals, space"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="style">Style</Label>
                    <Select value={testRequest.style} onValueChange={(value) => setTestRequest(prev => ({ ...prev, style: value }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="funny">Funny</SelectItem>
                        <SelectItem value="adventurous">Adventurous</SelectItem>
                        <SelectItem value="gentle">Gentle</SelectItem>
                        <SelectItem value="magical">Magical</SelectItem>
                        <SelectItem value="educational">Educational</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="lesson">Lesson</Label>
                    <Input
                      id="lesson"
                      value={testRequest.lesson}
                      onChange={(e) => setTestRequest(prev => ({ ...prev, lesson: e.target.value }))}
                      placeholder="kindness"
                    />
                  </div>
                </div>

                <Button onClick={testApi} disabled={testing} className="w-full">
                  {testing ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Testing API...
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <Play className="w-4 h-4 mr-2" />
                      Test API
                    </div>
                  )}
                </Button>

                {testResponse && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Response</h3>
                    <div className="bg-gray-100 p-4 rounded-lg">
                      <div className="mb-2">
                        <Badge variant={testResponse.status === 200 ? "default" : "destructive"}>
                          {testResponse.status} {testResponse.statusText}
                        </Badge>
                      </div>
                      <pre className="text-sm overflow-x-auto">
                        {JSON.stringify(testResponse.data, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Code Examples */}
      <div className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Code Examples
          </h2>
          <Tabs value={codeExamples[selectedExample].language} onValueChange={(value) => {
            const index = codeExamples.findIndex(ex => ex.language === value);
            setSelectedExample(index);
          }}>
            <TabsList className="grid w-full grid-cols-3">
              {codeExamples.map((example) => (
                <TabsTrigger key={example.language} value={example.language}>
                  {example.title}
                </TabsTrigger>
              ))}
            </TabsList>
            {codeExamples.map((example) => (
              <TabsContent key={example.language} value={example.language}>
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle>{example.title}</CardTitle>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyToClipboard(example.code)}
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        Copy
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
                      <code>{example.code}</code>
                    </pre>
                  </CardContent>
                </Card>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </div>

      {/* Endpoints Reference */}
      <div className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            API Reference
          </h2>
          <div className="space-y-6">
            {endpoints.map((endpoint, index) => (
              <Card key={index}>
                <CardHeader>
                  <div className="flex items-center gap-4">
                    <Badge variant={endpoint.method === 'GET' ? 'default' : 'secondary'}>
                      {endpoint.method}
                    </Badge>
                    <code className="text-lg font-mono">{endpoint.path}</code>
                  </div>
                  <CardDescription className="text-base">
                    {endpoint.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <h4 className="font-semibold mb-3">Parameters</h4>
                  <div className="space-y-2">
                    {endpoint.parameters.map((param, paramIndex) => (
                      <div key={paramIndex} className="flex items-center gap-4 text-sm">
                        <code className="bg-gray-100 px-2 py-1 rounded">{param.name}</code>
                        <Badge variant="outline">{param.type}</Badge>
                        {param.required && <Badge variant="destructive">Required</Badge>}
                        <span className="text-gray-600">{param.description}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-16 px-4 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Start Building?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Get your API key and start creating amazing applications today
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild size="lg" variant="secondary">
              <a href="/developer-signup">
                <Code className="w-4 h-4 mr-2" />
                Get API Key
              </a>
            </Button>
            <Button asChild size="lg">
              <a href="mailto:developers@snugglecrafters.com">
                <ExternalLink className="w-4 h-4 mr-2" />
                Contact Support
              </a>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}